import {
  Flex,
  HStack,
  Text,
  useColorModeValue,
  VStack,
  IconButton,
  Tooltip,
} from '@chakra-ui/react';
import React from 'react';

import { DataPipeline } from '@finley-technologies/finley-types';

import {
  useMeshDocument,
  useMeshRunRecord,
} from '../../hooks/instanceGraphContext';
import { colors } from 'packages/common-frontend/colors';
import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { useSlugToName } from 'packages/dataPipeline/hooks/slugToNameContext';
import { SelectedMeshRun } from 'packages/dataPipeline/types/dataPipelineTypes';
import { Pill } from 'packages/dataPipeline/ui/components/Pill';
import { PipelineDetailsAlert } from 'packages/dataPipeline/ui/details/PipelineDetailsAlert';
import { UploadDrawerExistingRun } from 'packages/dataPipeline/ui/uploads/UploadDrawer';
import { getReportingDateVersionString } from 'packages/dataPipeline/ui/utils/getReportingDateVersionString';

export const GraphHeader = ({ organizationId }: { organizationId: string }) => {
  const { cfSlugToName, lpSlugToName } = useSlugToName();

  const meshDocument = useMeshDocument();
  const { meshRunId, reportingDate, fileVersion } = useMeshRunRecord();

  const [uploadDrawerOpen, setUploadDrawerOpen] = React.useState(false);
  const headerTextColor = useColorModeValue('black', 'white');

  const reportingDateVersionStr = getReportingDateVersionString(
    reportingDate,
    fileVersion,
  );

  const selectedMeshRun: SelectedMeshRun = {
    meshRunId,
    meshRunParams: {
      rdfv: {
        reportingDate,
        fileVersion,
      },
      meshDocumentMetadata: DataPipeline.toMeshDocumentMetadata(meshDocument),
    },
  };

  return (
    <>
      <Flex alignItems="baseline" justifyContent="space-between" width="100%">
        <Text
          mt="0.75rem"
          mr="1rem"
          fontSize={36}
          fontWeight={600}
          color={headerTextColor}
          wordBreak="break-all"
        >
          ID: {meshRunId}
        </Text>
        <Tooltip label="Inputs">
          <IconButton
            aria-label="Inputs"
            key="Updates"
            icon={<FaIcon icon="file-chart-column" size="16px" />}
            _focus={{ boxShadow: 'none' }}
            onClick={() => setUploadDrawerOpen(true)}
          />
        </Tooltip>
      </Flex>
      <Text mt="10px" fontSize={14} fontWeight={500} color="gray" size="14px">
        {reportingDateVersionStr}
      </Text>

      <VStack mb="10px" alignItems="flex-start" width="100%">
        <Flex
          alignItems="center"
          mt="24px"
          justifyContent="space-between"
          width="100%"
        >
          <HStack spacing="0.5rem" flexWrap="wrap">
            {meshDocument.cfSlugs.map((slug) => {
              return (
                <Pill
                  key={slug}
                  icon={
                    <FaIcon icon="building-columns" color={colors.black[70]} />
                  }
                  label={cfSlugToName(slug)}
                />
              );
            })}
            {meshDocument.lpSlugs.map((slug) => {
              return (
                <Pill
                  key={slug}
                  icon={<FaIcon icon="shapes" color={colors.black[70]} />}
                  label={lpSlugToName(slug)}
                />
              );
            })}
          </HStack>
        </Flex>
        <PipelineDetailsAlert openUpload={() => setUploadDrawerOpen(true)} />
        {uploadDrawerOpen && (
          <UploadDrawerExistingRun
            selectedMeshRun={selectedMeshRun}
            organizationId={organizationId}
            onClose={() => setUploadDrawerOpen(false)}
          />
        )}
      </VStack>
    </>
  );
};
